import React, { useMemo } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';

import styles from './agentVariables.module.scss';

type Props = {
  label?: string;
  onChange: ( value: string ) => void;
  readonlyKeys?: boolean; // If true, keys are not editable and add/delete buttons are hidden
  showKeyInfo?: boolean; // If true, shows the keyInfo field next to key
  value: string | undefined;
};
type Variable = {
  key: string;
  value: string;
  keyInfo?: string; // Optional type information
};

const AgentVariables = ( { label = 'Variables', onChange, readonlyKeys = false, showKeyInfo = false, value }: Props ) => {
  const variables: Array<Variable> = useMemo( () => {
    try {
      return value ? JSON.parse( value ) : [];
    } catch {
      return [];
    }
  }, [ value ] );

  // Convert label to lowercase for test IDs
  const testIdPrefix = label.toLowerCase();

  const onAdd = () => {
    const next = [ ...variables, { key: '', value: '' } ];
    onChange( JSON.stringify( next ) );
  };
  const onRemove = ( index: number ) => {
    const next = variables.filter( ( _, i ) => i !== index );
    onChange( JSON.stringify( next ) );
  };
  const onKeyChange = ( newKey: string, index: number ) => {
    const next = variables.map( ( item, i ) => ( i === index ? { ...item, key: newKey } : item )
    );
    onChange( JSON.stringify( next ) );
  };

  const onValueChange = ( newValue: string, index: number ) => {
    const next = variables.map( ( item, i ) => ( i === index ? { ...item, value: newValue } : item )
    );
    onChange( JSON.stringify( next ) );
  };

  return (
    <>
      <div className={ styles.item } data-testid={ `agent-variables-${ testIdPrefix }-container` }>
        <div className={ styles.header } data-testid={ `agent-variables-${ testIdPrefix }-header` }>
          <label data-testid={ `agent-variables-${ testIdPrefix }-label` }>{ label }</label>
          {
            !readonlyKeys && (
              <a onClick={ onAdd } data-testid={ `agent-variables-${ testIdPrefix }-add-button` }><FaPlus/></a>
            )
          }
        </div>

        {variables.map( ( { key, value, keyInfo }, index ) => (
          <div key={ `${ index }` } className={ styles.variableItem } data-testid={ `agent-variables-${ testIdPrefix }-item-${ index }` }>
            <div className={ styles.keyRow }>
              <input
                type="text"
                value={ key }
                placeholder="name"
                onChange={ ( e ) => onKeyChange( e.target.value, index ) }
                disabled={ readonlyKeys }
                data-testid={ `agent-variables-${ testIdPrefix }-item-${ index }-key-input` }
              />
              {
                showKeyInfo && keyInfo && (
                  <span className={ styles.keyInfo } data-testid={ `agent-variables-${ testIdPrefix }-item-${ index }-key-info` }>{ keyInfo }</span>
                )
              }
              {
                !readonlyKeys && (
                  <a onClick={ ()=>onRemove( index ) } data-testid={ `agent-variables-${ testIdPrefix }-item-${ index }-delete-button` }><FaTrash /></a>
                )
              }
            </div>
            <div className={ styles.valueRow }>
              <textarea
                value={ value }
                placeholder="my value"
                onChange={ ( e ) => onValueChange( e.target.value, index ) }
                data-testid={ `agent-variables-${ testIdPrefix }-item-${ index }-value-textarea` }
              />
            </div>
          </div>
        ) )}
      </div>
    </>
  );
};

export default AgentVariables;